// src/pages/markets/board/card/body/MarketBlock.tsx
//
// Dispatches on market_mode, which the backend derives from the outcomes.
// dependent = the event's markets sum to 1.0 (neg risk), independent = several
// can resolve YES at once, so nothing sums.
//
// The ladder is now the card's primary navigation: clicking a row selects that
// market, and the conviction row below reads from the selection. It also
// carries the conviction itself — dominant capital per market, in the side's
// colour — so "which leg is the money on" is answered without selecting
// anything. That column replaced the score block above it.
//
// The distribution bar for dependent events sits *below* the ladder: it is a
// summary of the rows, and a summary reads after the thing it summarises.
//
// Everything clickable in here lives inside the card's root <a>, so every
// handler prevents default AND stops propagation.

import { numSx } from '../../../../../components/common/page/numberStyles';
import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { BoardScore, MarketSeries, ScreenerEvent, ScreenerMarket } from '../../api/boardTypes';
import { netCapital, netSide } from '../../api/netCapital';
import { DEPENDENT_BAR_HEIGHT, MARKET_BLOCK_HEIGHT, OUTCOME_ROW_HEIGHT } from '../cardLayout';
import {
    EM_DASH,
    formatProb,
    formatUsd,
    priceMeta,
    priceValue,
    truncate,
} from '../cardFormat';
import { sideLabels } from '../../utils/boardOutcomeLabels';
import { SingleMarketBlock } from './SingleMarketBlock';
import { sideColor } from '../../utils/sideColor';

// The ladder's capital cell is narrow and repeats down every row; a long team
// name would crowd out the market label beside it.
const OUTCOME_MAXLEN_LADDER = 14;

export interface MarketBlockProps {
    event: ScreenerEvent;
    selectedMarketId: string | null;
    onSelectMarket: (marketId: string) => void;
    // Overrides the default frame height; the New column hands the ladder the
    // whole body minus the fact strip.
    height?: number;
    // Scores of the active group, by market_id. Supplies the capital column and
    // the accent dot — a market without an entry simply has no conviction.
    scoreByMarket?: Map<string, BoardScore>;
    accentColor?: string;
    /** The selected market's true outcome pair, when the card has fetched it. */
    outcomes?: string[] | null;
    /** Every market's true pair, so each ladder row can name its own side. */
    outcomesByMarket?: ReadonlyMap<string, string[]>;
    /** Price history for the selected market, when the column has it. */
    series?: MarketSeries | null;
    seriesWindow?: string;
    group?: string | null;
    eventId?: string;
    now?: number;
}

const SEGMENT_KEYS = ['primary', 'success', 'warning', 'info', 'secondary', 'error'] as const;

// Fixed, not auto: every column right of the market label has to line up down
// the whole ladder, so the bars and percentages read as columns. Wide enough
// for "$5.0k AURORA GAMING"; the market label (flex: 1) gives up the room.
const CAPITAL_COL = 112;
const PROB_COL = 32;
const BAR_COL = 40;

function useSegmentColors(): string[] {
    const theme = useTheme();
    return SEGMENT_KEYS.map(k => theme.palette[k].main);
}

function marketLabel(m: ScreenerMarket): string {
    return m.outcome_yes_label || m.question || m.market_id.slice(0, 8);
}

function byProbDesc(a: ScreenerMarket, b: ScreenerMarket): number {
    return (priceValue(b) ?? -1) - (priceValue(a) ?? -1);
}

// Selected market first, everything else by probability. Deterministic, and
// the pin is what keeps the active conviction's market on screen.
function orderPinned(markets: ScreenerMarket[], selectedId: string | null): ScreenerMarket[] {
    const sorted = [...markets].sort(byProbDesc);
    if (!selectedId) return sorted;
    const i = sorted.findIndex(m => m.market_id === selectedId);
    if (i <= 0) return sorted;
    const [sel] = sorted.splice(i, 1);
    return [sel, ...sorted];
}

function stopCardNav(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
}

function Frame({ height, children }: { height: number; children: React.ReactNode }) {
    return (
        <Box style={{ height }} sx={{ boxSizing: 'border-box', overflow: 'hidden' }}>
            {children}
        </Box>
    );
}

const scrollSx = {
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
} as const;

function ScoreDot({ color }: { color: string }) {
    return (
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    );
}

interface RowsProps {
    markets: ScreenerMarket[];
    selectedMarketId: string | null;
    onSelectMarket: (id: string) => void;
    scoreByMarket?: Map<string, BoardScore>;
    accentColor?: string;
    outcomesByMarket?: ReadonlyMap<string, string[]>;
    now: number;
}

// One market. The capital cell is the only coloured thing in the row, so the
// eye finds the side before it reads a word.
function MarketRows({
    markets,
    selectedMarketId,
    onSelectMarket,
    scoreByMarket,
    accentColor,
    outcomesByMarket,
    now,
}: RowsProps) {
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const ordered = orderPinned(markets, selectedMarketId);
    const hasScores = markets.some(m => scoreByMarket?.has(m.market_id));

    // Selected is at index 0; snap the scroll there so a fresh selection is
    // never below the fold.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [selectedMarketId]);

    return (
        <Box ref={scrollRef} sx={{ height: '100%', ...scrollSx }}>
            {ordered.map(m => {
                const selected = m.market_id === selectedMarketId;
                const price = priceMeta(m, now);
                const prob = price.value;
                const score = scoreByMarket?.get(m.market_id);
                const side = score ? netSide(score) : null;
                const capital = score ? Math.abs(netCapital(score)) : null;
                // Each row is its own market with its own pair.
                const rowLabels = sideLabels(m, OUTCOME_MAXLEN_LADDER, {
                    outcomes: outcomesByMarket?.get(m.market_id),
                });
                const rowSide = side === 'NO' ? rowLabels.no : rowLabels.yes;
                const capitalColor = sideColor(rowLabels, side, theme, { muted: true });

                return (
                    <Box
                        key={m.market_id}
                        onClick={e => {
                            stopCardNav(e);
                            onSelectMarket(m.market_id);
                        }}
                        title={m.question ?? undefined}
                        style={{ height: OUTCOME_ROW_HEIGHT }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 0.25,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            opacity: m.is_open ? 1 : 0.45,
                            bgcolor: selected
                                ? alpha(theme.palette.primary.main, 0.14)
                                : 'transparent',
                            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.07) },
                        }}
                    >
                        <ScoreDot
                            color={
                                score && accentColor
                                    ? accentColor
                                    : alpha(theme.palette.text.primary, 0.14)
                            }
                        />

                        <Typography
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: '11px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: selected ? 700 : 400,
                                color: selected ? 'text.primary' : 'text.secondary',
                            }}
                        >
                            {truncate(marketLabel(m), 40)}
                        </Typography>

                        <Box
                            sx={{
                                width: BAR_COL,
                                height: 4,
                                flexShrink: 0,
                                borderRadius: '2px',
                                overflow: 'hidden',
                                bgcolor: alpha(theme.palette.text.primary, 0.12),
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${Math.max(0, Math.min(1, prob ?? 0)) * 100}%`,
                                    height: '100%',
                                    bgcolor: selected ? 'primary.main' : 'text.secondary',
                                }}
                            />
                        </Box>

                        <Typography
                            title={price.title}
                            sx={{
                                width: PROB_COL,
                                flexShrink: 0,
                                fontSize: '11px',
                                textAlign: 'right',
                                ...numSx,
                                color: selected ? 'text.primary' : 'text.secondary',
                            }}
                        >
                            {prob === null ? EM_DASH : formatProb(prob)}
                        </Typography>

                        {hasScores && (
                            <Typography
                                title={capital === null ? undefined : rowSide.title}
                                sx={{
                                    width: CAPITAL_COL,
                                    flexShrink: 0,
                                    fontSize: '11px',
                                    fontWeight: capital === null ? 400 : 700,
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    ...numSx,
                                    color: capitalColor,
                                }}
                            >
                                {capital === null
                                    ? EM_DASH
                                    : side === null
                                        ? formatUsd(capital)
                                        : `${formatUsd(capital)} ${rowSide.text}`}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

// Dependent (neg-risk) events: the ladder, then a 100% distribution bar under
// it. Segments stay clickable — the bar is a second way to reach the same
// selection, useful when one outcome dominates the width.
function DependentMarkets({
    markets,
    selectedMarketId,
    onSelectMarket,
    scoreByMarket,
    accentColor,
    outcomesByMarket,
    now,
}: RowsProps) {
    const theme = useTheme();
    const colors = useSegmentColors();
    const sorted = [...markets].sort(byProbDesc);
    const anySelected = Boolean(selectedMarketId);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <MarketRows
                    markets={markets}
                    selectedMarketId={selectedMarketId}
                    onSelectMarket={onSelectMarket}
                    scoreByMarket={scoreByMarket}
                    accentColor={accentColor}
                    outcomesByMarket={outcomesByMarket}
                    now={now}
                />
            </Box>

            <Box
                style={{ height: DEPENDENT_BAR_HEIGHT }}
                sx={{ flexShrink: 0, borderRadius: '3px', overflow: 'hidden', display: 'flex' }}
            >
                {sorted.map((m, i) => {
                    const price = priceMeta(m, now);
                    const share = Math.max(0, Math.min(1, price.value ?? 0));
                    if (share <= 0) return null;
                    const selected = m.market_id === selectedMarketId;
                    return (
                        <Box
                            key={m.market_id}
                            title={`${marketLabel(m)} — ${formatProb(price.value)} · ${price.title}`}
                            onClick={e => {
                                stopCardNav(e);
                                onSelectMarket(m.market_id);
                            }}
                            sx={{
                                width: `${share * 100}%`,
                                bgcolor: colors[i % colors.length],
                                cursor: 'pointer',
                                // Non-selected segments step back so the
                                // clicked one pops without a layout change.
                                opacity: !anySelected || selected ? 1 : 0.35,
                                boxShadow: selected
                                    ? `inset 0 0 0 1.5px ${theme.palette.text.primary}`
                                    : 'none',
                                transition: 'opacity 120ms',
                                '&:hover': { opacity: 1 },
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

export function MarketBlock({
    event,
    selectedMarketId,
    onSelectMarket,
    height = MARKET_BLOCK_HEIGHT,
    scoreByMarket,
    accentColor,
    outcomes,
    outcomesByMarket,
    series,
    seriesWindow,
    group,
    eventId,
    now,
}: MarketBlockProps) {
    const markets = event.markets;
    const nowMs = now ?? Date.now();

    if (markets.length === 0) {
        return (
            <Frame height={height}>
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    No market data
                </Typography>
            </Frame>
        );
    }

    if (event.market_mode === 'dependent' && markets.length > 1) {
        return (
            <Frame height={height}>
                <DependentMarkets
                    markets={markets}
                    selectedMarketId={selectedMarketId}
                    onSelectMarket={onSelectMarket}
                    scoreByMarket={scoreByMarket}
                    accentColor={accentColor}
                    outcomesByMarket={outcomesByMarket}
                    now={nowMs}
                />
            </Frame>
        );
    }

    if (event.market_mode === 'independent' && markets.length > 1) {
        return (
            <Frame height={height}>
                <MarketRows
                    markets={markets}
                    selectedMarketId={selectedMarketId}
                    onSelectMarket={onSelectMarket}
                    scoreByMarket={scoreByMarket}
                    accentColor={accentColor}
                    outcomesByMarket={outcomesByMarket}
                    now={nowMs}
                />
            </Frame>
        );
    }

    const selected = markets.find(m => m.market_id === selectedMarketId) ?? markets[0] ?? null;

    return (
        <Frame height={height}>
            <SingleMarketBlock
                market={selected}
                score={selected ? scoreByMarket?.get(selected.market_id) : undefined}
                outcomes={outcomes}
                height={height}
                series={series}
                seriesWindow={seriesWindow}
                group={group}
                eventId={eventId ?? event.event_id}
                now={now}
            />
        </Frame>
    );
}